import React, { useState, useEffect, useMemo } from "react";
import { Global } from "../helpers/Global";

// 👁️ FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

export const RegistrarUsuarioDNI = () => {
  const [managers, setManagers] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: "",
    apellidos: "",
    dni: "",
    email: "",
    password: "",
    rol: "usuario",
    gerente_id: "",
    telefono: ""
  });
  const [confirmPassword, setConfirmPassword] = useState("");

  // 🆕 archivo de avatar (opcional)
  const [avatarFile, setAvatarFile] = useState(null);

  const pwdMismatch = useMemo(
    () =>
      (nuevoUsuario.password?.length ?? 0) > 0 ||
      (confirmPassword?.length ?? 0) > 0
        ? nuevoUsuario.password !== confirmPassword
        : false,
    [nuevoUsuario.password, confirmPassword]
  );

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${Global.url}usuarios`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setManagers(data.filter((u) => u.rol === "gerente"));
        }
      } catch (err) {
        console.error("Error al obtener managers:", err);
      }
    })();
  }, []);

  const registrarUsuario = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!nuevoUsuario.password) {
      setMensaje("❌ La contraseña es obligatoria.");
      return;
    }
    if (nuevoUsuario.password.length < 8) {
      setMensaje("❌ La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (pwdMismatch) {
      setMensaje("❌ Las contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    setMensaje("");

    const payload = {
      ...nuevoUsuario,
      dni: (nuevoUsuario.dni || "").toUpperCase().replace(/\s|-/g, ""),
      email: (nuevoUsuario.email || "").trim().toLowerCase() || null,
      gerente_id: nuevoUsuario.gerente_id || null,
      telefono: (nuevoUsuario.telefono || "").trim() || null,
    };

    try {
      const response = await fetch(`${Global.url}usuarios/admin/register-dni`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMensaje(`❌ ${data?.message || "Error al registrar el usuario."}`);
        return;
      }

      // ✅ Usuario creado correctamente
      const createdUser = data.user;
      setMensaje("✅ Usuario registrado correctamente.");

      // 🖼️ Si hay avatar seleccionado, lo subimos ahora
      if (createdUser?.id && avatarFile) {
        try {
          const formData = new FormData();
          formData.append("avatar", avatarFile);

          const avatarRes = await fetch(`${Global.url}usuarios/${createdUser.id}/avatar`, {
            method: "POST",
            headers: {
              // ⚠️ NO poner "Content-Type": "application/json" aquí
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: formData,
          });

          const avatarData = await avatarRes.json().catch(() => ({}));
          if (!avatarRes.ok) {
            console.error("Error al subir avatar:", avatarData);
            // No rompemos el flujo; el usuario está creado, solo avisamos si quieres
            setMensaje((prev) => (prev ? prev + " (avatar no subido)" : "Avatar no subido."));
          } else {
            // Opcional: podrías actualizar aquí algún estado global si usas contexto de usuarios
            setMensaje("✅ Usuario registrado y avatar subido correctamente.");
          }
        } catch (err) {
          console.error("Error de red al subir avatar:", err);
          setMensaje((prev) => (prev ? prev + " (error subiendo avatar)" : "Error subiendo avatar."));
        }
      }

      // Resetear formulario
      setNuevoUsuario({
        nombre: "",
        apellidos: "",
        dni: "",
        email: "",
        password: "",
        rol: "usuario",
        gerente_id: "",
        telefono: ""
      });
      setConfirmPassword("");
      setAvatarFile(null);

      setTimeout(() => setMensaje(""), 2500);
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      setMensaje("❌ Error de conexión con el servidor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🆕 handler cambio de archivo
  const onAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFile(null);
      return;
    }
    // Opcional: validaciones básicas de tipo/tamaño
    if (!file.type.startsWith("image/")) {
      setMensaje("❌ El archivo seleccionado no es una imagen.");
      setAvatarFile(null);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setMensaje("❌ La imagen no puede superar los 3MB.");
      setAvatarFile(null);
      return;
    }
    setAvatarFile(file);
  };

  return (
    <div className="form-crear-usuario">
      <h2>Registrar Nuevo Usuario</h2>

      {mensaje && (
        <p className={`mensaje ${mensaje.includes("❌") ? "rojo" : "verde"}`}>{mensaje}</p>
      )}

      <form onSubmit={registrarUsuario} className="formulario-usuario">
        <div className="form-group">
          <label>DNI (obligatorio):</label>
          <input
            type="text"
            value={nuevoUsuario.dni}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, dni: e.target.value })}
            required
            placeholder="12345678Z"
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label>Nombre:</label>
          <input
            type="text"
            value={nuevoUsuario.nombre}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value })}
            required
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label>Apellidos:</label>
          <input
            type="text"
            value={nuevoUsuario.apellidos}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, apellidos: e.target.value })}
            required
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label>Email (opcional):</label>
          <input
            type="email"
            value={nuevoUsuario.email}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, email: e.target.value })}
            placeholder="usuario@dominio.com"
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label>Teléfono (opcional):</label>
          <input
            type="text"
            value={nuevoUsuario.telefono}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, telefono: e.target.value })}
            placeholder="Ej: 600 123 456"
            autoComplete="off"
          />
        </div>

        {/* 🆕 Avatar (opcional) */}
        <div className="form-group">
          <label>Foto / Avatar (opcional):</label>
          <input
            type="file"
            accept="image/*"
            onChange={onAvatarChange}
          />
          {avatarFile && (
            <small className="muted">
              Seleccionado: {avatarFile.name} ({Math.round(avatarFile.size / 1024)} KB)
            </small>
          )}
        </div>

        {/* Contraseña */}
        <div className="form-group">
          <label>Contraseña:</label>
          <div className="input-with-button">
            <input
              type={showPassword ? "text" : "password"}
              value={nuevoUsuario.password}
              onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
            />
            <button
              type="button"
              className="btn-eye"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              <span className="sr-only">{showPassword ? "Ocultar" : "Mostrar"}</span>
            </button>
          </div>
        </div>

        {/* Confirmar contraseña */}
        <div className="form-group">
          <label>Confirmar contraseña:</label>
          <div className="input-with-button">
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Repite la contraseña"
            />
            <button
              type="button"
              className="btn-eye"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              <span className="sr-only">{showPassword ? "Ocultar" : "Mostrar"}</span>
            </button>
          </div>
          {pwdMismatch && (
            <small className="rojo">Las contraseñas no coinciden.</small>
          )}
        </div>

        <div className="form-group">
          <label>Rol:</label>
          <select
            value={nuevoUsuario.rol}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, rol: e.target.value })}
            required
          >
            <option value="usuario">Usuario</option>
            <option value="gerente">Gerente</option>
            <option value="admin">Administrador</option>
          </select>
        </div>

        <div className="form-group">
          <label>Manager (opcional):</label>
          <select
            value={nuevoUsuario.gerente_id}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, gerente_id: e.target.value })}
          >
            <option value="">Sin manager</option>
            {managers.map((g) => (
              <option key={g.id} value={g.id}>
                {g.nombre} {g.apellidos}
              </option>
            ))}
          </select>
        </div>

        <button
          className="btn-login"
          type="submit"
          disabled={isSubmitting || pwdMismatch}
          title={pwdMismatch ? "Las contraseñas no coinciden" : undefined}
        >
          {isSubmitting ? "Registrando..." : "Registrar"}
        </button>
      </form>
    </div>
  );
};
